import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, api } from '@/lib/api';
import { TimezonePicker } from '@/components/shared/TimezonePicker';
import { browserTimezone } from '@/lib/timezones';
import { AuthLayout } from './AuthLayout';
import { useAuth } from './AuthProvider';

interface InvitePreview {
  email: string;
  workspaceName: string;
  /** V10 §4 — what the picker defaults to, since most joiners share it. */
  workspaceTimezone: string;
  inviterName: string;
  designation: string;
  systemRole: string;
  expiresAt: string;
}



export function InvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { applySession } = useAuth();

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [timezone, setTimezone] = useState(browserTimezone());
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await api.get<InvitePreview>(`/auth/invitations/${token}`);
        if (cancelled) return;
        setPreview(data);
        /**
         * V10 §4 — "defaulting to the workspace timezone".
         *
         * The browser's guess is only what the field holds while the preview
         * is in flight; the workspace's own zone is the answer for almost
         * everybody joining it, and the ones it is wrong for are exactly the
         * ones who will open the picker.
         */
        if (data.workspaceTimezone) setTimezone(data.workspaceTimezone);
      } catch (err) {
        if (cancelled) return;
        setLoadError(
          err instanceof ApiError ? err.message : 'Could not load this invitation.',
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await api.post<{ accessToken: string }>(
        `/auth/invitations/${token}/accept`,
        { name: name.trim(), password, timezone },
        { noRetry: true },
      );
      await applySession(data.accessToken);
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (err.details?.[0]?.message ?? err.message)
          : 'Could not reach the server. Try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (loadError) {
    return (
      <AuthLayout title="Invitation not valid" subtitle={loadError}>
        <p className="mb-4 text-[13px] vt-muted">
          Invitations expire after 7 days and can be used once. Ask an admin to send a new one.
        </p>
        <Button asChild variant="secondary" size="md" className="w-full">
          <Link to="/login">Go to sign in</Link>
        </Button>
      </AuthLayout>
    );
  }

  if (!preview) {
    return (
      <AuthLayout title="Loading invitation">
        <div className="flex flex-col gap-3">
          <Skeleton className="h-8" />
          <Skeleton className="h-8" />
          <Skeleton className="h-9" />
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={`Join ${preview.workspaceName}`}
      subtitle={`${preview.inviterName} invited ${preview.email} as ${preview.designation.replace(/_/g, ' ')}.`}
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="name">Your name</Label>
          <Input
            id="name"
            required
            autoFocus
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="text-[11px] vt-muted">At least 10 characters.</p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="timezone">Timezone</Label>
          {/* V10 §4 — the same control as Profile and Workspace settings. */}
          <TimezonePicker
            id="timezone"
            value={timezone}
            onChange={setTimezone}
            suggested={preview.workspaceTimezone}
          />
        </div>

        {error && (
          <p role="alert" className="rounded-[6px] bg-[var(--danger)]/10 px-2.5 py-2 text-[12px] text-[var(--danger)]">
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" size="lg" disabled={submitting}>
          {submitting ? <Loader2 className="animate-spin" /> : null}
          Accept and get started
        </Button>
      </form>
    </AuthLayout>
  );
}
